import React, { useState } from 'react';
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import LxApprovalButtons, { getMilestonePlanApprovalBtn, getValuesApprovalBtn, getKPIApprovalBtn, getReturnToPriorPhaseBtn } from "./LxApprovalButtons";

import { meetsMilestoneValidationRequirements } from '../../utils/validationUtils';

const useStyles = makeStyles(() => ({
  headerSummary: {
    width: '100%',
    height: '40px',
    lineHeight: '28px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  statusBackground: {
    backgroundColor: 'rgba(110,247,138,0.55)'
  },
  cancelledLHoldBackground: {
    backgroundColor: 'rgba(255, 190, 59, 0.6)'
  },
  hoverClickBackground: {
    backgroundColor: 'rgba(88,224,246,1)'
  },
  svgBox: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    height: '275px'
  },
  noApprovals: {
    height: '100px'
  }
}));

const revertClicked = {
  Build: 'Aim',
  Confirm: 'Build',
  Deliver: 'Confirm',
  Embed: 'Deliver'
};

export default function PhaseCard({ initiative, onSuccess, milestones, kpis }) {
  const [hover, setHover] = useState(null);
  const [wasClicked, setWasClicked] = useState(null);

  function requiresGreaterSponsor(initiative) {
    return initiative.classification === 3; // TODO: Make this more robust
  }

  const isStateApproved = (state) => {
    switch (state) {
      case 'Build':
        return initiative.build_sponsor_approval != null;
      case 'KPI':
        return initiative.kpi_wsl_approval != null || initiative.kpi_to_approval != null;
      case 'Milestone':
        return initiative.milestone_wsl_approval != null;
      case 'Value':
        //BUG5095 5095 - remove and condition from wsl and finance approver
        return initiative.cashFlow ? ((initiative.value_wsl_approval != null || initiative.value_finance_approval != null) || initiative.confirm_request_approval) :
          initiative.value_wsl_approval != null;
      case 'Confirm':
        return requiresGreaterSponsor(initiative) ?
          ((initiative.confirm_to_approval != null) && (initiative.confirm_sponsor_approval != null)) :
          (initiative.confirm_sponsor_approval != null);
      case 'Deliver':
        return requiresGreaterSponsor(initiative) ?
          ((initiative.deliver_to_approval != null) && (initiative.deliver_sponsor_approval != null)) :
          (initiative.deliver_sponsor_approval != null);
      case 'Embed':
        return requiresGreaterSponsor(initiative) ?
          (initiative.embed_to_approval != null) && (initiative.embed_sponsor_approval != null) :
          (initiative.embed_sponsor_approval != null);
      default:
        return false;
    }
  };

  const isStateRequested = (state) => {
    switch (state) {
      case 'Build':
        return !isStateApproved(state) && initiative.build_request_approval;
      case 'KPI':
        return !isStateApproved(state) && initiative.kpi_request_approval;
      case 'Milestone':
        return !isStateApproved(state) && initiative.milestone_request_approval;
      case 'Value':
        return !isStateApproved(state) && initiative.value_request_approval;
      case 'Confirm':
        return !isStateApproved(state) && initiative.confirm_request_approval;
      case 'Deliver':
        return !isStateApproved(state) && initiative.deliver_request_approval;
      case 'Embed':
        return !isStateApproved(state) && initiative.embed_request_approval;
      default:
        return false;
    }
  };

  const mouseOverEvent = (type) => () => {
    if (isPhaseClickable(type) || isRevertPhaseClickable(type)) {
      setHover(type);
    }
  };

  const mouseLeaveEvent = () => {
    setHover(null);
  };

  const handleClick = (type) => () => {
    if (!showForwardHover(type) && !isRevertPhaseClickable(type)) {
      return;
    }
    if (type === wasClicked) {
      setWasClicked(null);
    } else {
      setWasClicked(type);
    }
  };
  const showForwardHover = (type) => {
    if (!hover && !wasClicked) {
      return false;
    }

    if (type !== hover && type !== wasClicked) {
      return false;
    }
    return isPhaseClickable(type);
  }

  const isPhaseClickable = (type) => {
    switch (type) {
      case 'Build':
        return initiative && initiative._links && (initiative._links['BUILD_REQUEST_APPROVAL']);
      case 'KPI':
        const kpi = kpis[0];
        return initiative && initiative._links && initiative.short_name === 'Build' &&
          kpi.unit_measure && kpi.target && kpi.baseline &&
          (initiative._links['KPI_REQUEST_APPROVAL'] || initiative._links['KPI_TO_APPROVAL'] || initiative._links['KPI_WSL_APPROVAL']);
      case 'Milestone':
        return initiative && initiative._links && initiative.short_name === 'Build'
          && milestones && meetsMilestoneValidationRequirements(milestones) &&
          (initiative._links['MILESTONE_REQUEST_APPROVAL'] || initiative._links['MILESTONE_WSL_APPROVAL']);
      case 'Value':
        return initiative && initiative._links && initiative.short_name === 'Build' &&
          (initiative._links['VALUE_REQUEST_APPROVAL'] || initiative._links['VALUE_TO_APPROVAL']
            || initiative._links['VALUE_WSL_APPROVAL'] || initiative._links['VALUE_FINANCE_APPROVAL']);
      case 'Confirm':
        return initiative && initiative._links &&
          (initiative._links['CONFIRM_REQUEST_APPROVAL']);
      case 'Deliver':
        return initiative && initiative._links &&
          (initiative._links['DELIVER_REQUEST_APPROVAL']);
      case 'Embed':
        return initiative && initiative._links &&
          (initiative._links['EMBED_REQUEST_APPROVAL']);
      default:
        return false;
    }
  };

  const isRevertAllowed = (type) => {
    if (!hover && !wasClicked) {
      return false;
    }

    if (type !== hover && type !== wasClicked) {
      return false;
    }
    return isRevertPhaseClickable(type);
  }

  const isRevertPhaseClickable = (type) => {
    switch (type) {
      case 'Build':
        return initiative && initiative._links && (initiative._links['AIM_ROLLBACK_APPROVAL']);
      case 'Confirm':
        return initiative && initiative._links && (initiative._links['BUILD_ROLLBACK_APPROVAL']);
      case 'Deliver':
        return initiative && initiative._links && (initiative._links['CONFIRM_ROLLBACK_APPROVAL']);
      case 'Embed':
        return initiative && initiative._links && (initiative._links['DELIVER_ROLLBACK_APPROVAL']);
      default:
        return false;
    }
  };

  const getSubmitStatusMessage = () => {
    const isCheckpoint = wasClicked === 'KPI' || wasClicked === 'Milestone' || wasClicked === 'Value'
      || hover === 'KPI' || hover === 'Milestone' || hover === 'Value';
    if (isPhaseClickable(wasClicked)) {
      const approvalType = isPhaseClickable(wasClicked);
      if (approvalType && approvalType.href.indexOf('_REQUEST') !== -1) {
        if (isCheckpoint) {
          return `CONFIRM VALIDATE ${wasClicked.toUpperCase()}`;
        }
        return `CONFIRM SUBMIT TO ${wasClicked.toUpperCase()}`;
      } else {
        if (isCheckpoint) {
          return `CONFIRM VALIDATION FOR ${wasClicked.toUpperCase()}`;
        }
        return `CONFIRM APPROVE TO ${wasClicked.toUpperCase()}`;
      }
    } else if (isPhaseClickable(hover)) {
      const approvalType = isPhaseClickable(hover);
      if (approvalType && approvalType.href.indexOf('_REQUEST') !== -1) {
        if (isCheckpoint) {
          return `SUBMIT FOR ${hover.toUpperCase()} VALIDATION`;
        }
        return `SUBMIT TO ${hover.toUpperCase()}`;
      } else {
        if (isCheckpoint) {
          return `VALIDATE ${hover.toUpperCase()}`;
        }
        return `APPROVE TO ${hover.toUpperCase()}`;
      }
    }
    if (isRevertPhaseClickable(wasClicked)) {
      return `CONFIRM RETURN TO ${revertClicked[wasClicked].toUpperCase()}`;
    } else if (isRevertPhaseClickable(hover)) {
      return `RETURN TO ${revertClicked[hover].toUpperCase()}`;
    }
  };

  const getKpiMilestoneValueSubmitMessaging = () => {
    if (initiative.short_name !== 'Build') {
      return [];
    }
    const messaging = [];
    if (isStateRequested('KPI')) {
      messaging.push('KPI VALIDATION');
    }
    if (isStateRequested('Milestone')) {
      messaging.push('MILESTONE PLAN VALIDATION');
    }
    if (isStateRequested('Value')) {
      messaging.push('VALUE PLAN VALIDATION');
    }

    return messaging;
  }

  const getStatusMessage = () => {
    const checkpointMessaging = getKpiMilestoneValueSubmitMessaging();
    if (checkpointMessaging.length > 0) {
      return `PENDING ${checkpointMessaging.join(', ')}`;
    }
    if (initiative && initiative.short_name && initiative.short_name.indexOf('Submitted') === -1) {
      return (`${initiative.status} PHASE`).toUpperCase();
    } else if (initiative && initiative.short_name && initiative.short_name.indexOf('Submitted') !== -1) {

      return (`PENDING ${initiative.short_name.substring(0, initiative.short_name.indexOf('Submitted'))} APPROVAL`).toUpperCase();
    }
    return initiative.status;
  }

  const handleSuccess = () => {
    setWasClicked(null);
    onSuccess();
  }

  const hasMoneyStep = () => milestones.find(item => item.type === 5);
  const getApprovalButtons = () => {
    if (!wasClicked && initiative.short_name && initiative.short_name.indexOf('Submitted') !== -1) {
      const level = initiative.short_name.substring(0, initiative.short_name.indexOf('Submitted'));
      return (
        <LxApprovalButtons
          initiative={initiative}
          onSuccess={handleSuccess}
          level={level.toUpperCase()}
          buttonText={'SUBMIT FOR APPROVAL'}
        />
      )
    }

    if (isRevertPhaseClickable(wasClicked)) {
      const value = revertClicked[wasClicked].toUpperCase() + '_ROLLBACK_APPROVAL';
      return getReturnToPriorPhaseBtn(initiative._links[value], handleSuccess);
    }

    if (wasClicked === 'Milestone') {
      return getMilestonePlanApprovalBtn(meetsMilestoneValidationRequirements(milestones), initiative, handleSuccess);
    }
    if (wasClicked === 'Value') {
      return getValuesApprovalBtn(initiative, handleSuccess);
    }
    if (wasClicked === 'KPI') {
      return getKPIApprovalBtn(initiative, handleSuccess);
    }

    if (wasClicked) {
      return (
        <LxApprovalButtons
          initiative={initiative}
          onSuccess={handleSuccess}
          level={wasClicked.toUpperCase()}
          buttonText={'SUBMIT FOR APPROVAL'}
        />
      );
    }

    return <div className={classes.noApprovals}></div>;
  };



  const classes = useStyles();
  return (<div>
    {wasClicked || hover ?
      <div width="100%" height="40" fill="#58e0f6" className={`${classes.headerSummary} ${classes.hoverClickBackground}`} key="status-bar">
        <Typography variant="h5" component="h2" className={classes.milestoneCardHeader}>
          {getSubmitStatusMessage()}
        </Typography>
      </div>
      :
      <div width="100%" height="40"
        className={`${classes.headerSummary} 
           ${initiative.short_name === 'Cancelled' || initiative.short_name === 'Hold' ?
            classes.cancelledLHoldBackground : classes.statusBackground}`} key="hover-and-click-bar">
        <Typography variant="h5" component="h2" className={classes.milestoneCardHeader}>
          {getStatusMessage()}
        </Typography>
      </div>
    }

    <div className={classes.svgBox}>
      <svg width="900" height="352" viewBox="113.85399627685547 55.91019821166992 783.4719848632812 192.0897979736328"
        fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="m128,88l168,0l0,48m224.5,-48l0,33c0,8.284 -6.716,15 -15,15l-209.5,0m224.5,-48l0,76c0,11.046 -8.954,20 -20,20l-204.5,0m224.5,-96l352.5,0m-577,48l0,48"
          stroke="#000f56" strokeWidth="4"></path>
        <path
          d="M120.313 63.6826H116.458L115.651 66H113.854L117.613 56.0469H119.165L122.932 66H121.127L120.313 63.6826ZM116.943 62.2881H119.828L118.386 58.1592L116.943 62.2881ZM127.392 66H125.669V56.0469H127.392V66ZM133.26 56.0469L136.131 63.6758L138.995 56.0469H141.23V66H139.508V62.7188L139.679 58.3301L136.739 66H135.502L132.569 58.3369L132.74 62.7188V66H131.018V56.0469H133.26Z"
          fill="black"></path>
        <path
          d="M274.693 66V56.0469H278.104C279.23 56.0469 280.087 56.2725 280.675 56.7236C281.263 57.1748 281.557 57.847 281.557 58.7402C281.557 59.196 281.434 59.6061 281.188 59.9707C280.941 60.3353 280.581 60.6178 280.107 60.8184C280.645 60.9642 281.06 61.2399 281.352 61.6455C281.648 62.0465 281.796 62.5296 281.796 63.0947C281.796 64.029 281.495 64.7467 280.894 65.248C280.297 65.7493 279.44 66 278.323 66H274.693ZM276.423 61.5088V64.6191H278.344C278.886 64.6191 279.31 64.4847 279.615 64.2158C279.921 63.9469 280.073 63.5732 280.073 63.0947C280.073 62.0602 279.545 61.5316 278.487 61.5088H276.423ZM276.423 60.2373H278.118C278.656 60.2373 279.075 60.1165 279.376 59.875C279.681 59.6289 279.834 59.2826 279.834 58.8359C279.834 58.3438 279.693 57.9883 279.41 57.7695C279.132 57.5508 278.697 57.4414 278.104 57.4414H276.423V60.2373ZM292.306 56.0469V62.6982C292.306 63.7555 291.966 64.5941 291.287 65.2139C290.613 65.8291 289.713 66.1367 288.587 66.1367C287.448 66.1367 286.543 65.8337 285.873 65.2275C285.203 64.6169 284.868 63.7715 284.868 62.6914V56.0469H286.591V62.7051C286.591 63.3704 286.759 63.8786 287.097 64.2295C287.434 64.5804 287.931 64.7559 288.587 64.7559C289.918 64.7559 290.583 64.054 290.583 62.6504V56.0469H292.306ZM297.483 66H295.761V56.0469H297.483V66ZM302.839 64.6191H307.357V66H301.109V56.0469H302.839V64.6191ZM310.184 66V56.0469H313.123C314.003 56.0469 314.782 56.2428 315.461 56.6348C316.145 57.0267 316.673 57.5827 317.047 58.3027C317.421 59.0228 317.607 59.8477 317.607 60.7773V61.2764C317.607 62.2197 317.418 63.0492 317.04 63.7646C316.666 64.4801 316.131 65.0316 315.434 65.4189C314.741 65.8063 313.946 66 313.048 66H310.184ZM311.913 57.4414V64.6191H313.041C313.948 64.6191 314.643 64.3366 315.126 63.7715C315.614 63.2018 315.862 62.3861 315.871 61.3242V60.7705C315.871 59.6904 315.636 58.8656 315.167 58.2959C314.698 57.7262 314.016 57.4414 313.123 57.4414H311.913Z"
          fill="black"></path>
        {/* Milestone Plan Label */}
        <path
          d="m 140.35,131.18456 2.871,7.629 2.864,-7.629 h 2.235 v 9.953 h -1.722 v -3.281 l 0.171,-4.389 -2.94,7.67 h -1.237 l -2.933,-7.663 0.171,4.382 v 3.281 h -1.723 v -9.953 z m 13.346,9.953 h -1.722 v -9.953 h 1.722 z m 5.356,-1.381 h 4.518 v 1.381 h -6.248 v -9.953 h 1.73 z m 13.162,-3.062 h -4.088 v 3.062 h 4.778 v 1.381 h -6.508 v -9.953 h 6.46 v 1.394 h -4.73 v 2.748 h 4.088 z m 8.801,1.88 c 0,-0.438 -0.155,-0.775 -0.465,-1.012 -0.306,-0.237 -0.859,-0.476 -1.661,-0.718 -0.802,-0.242 -1.44,-0.51 -1.914,-0.807 -0.907,-0.569 -1.361,-1.312 -1.361,-2.228 0,-0.802 0.326,-1.463 0.978,-1.983 0.656,-0.519 1.506,-0.779 2.55,-0.779 0.692,0 1.31,0.128 1.852,0.383 0.542,0.255 0.969,0.62 1.278,1.094 0.31,0.469 0.465,0.991 0.465,1.565 h -1.722 c 0,-0.519 -0.164,-0.925 -0.493,-1.217 -0.323,-0.296 -0.788,-0.444 -1.394,-0.444 -0.565,0 -1.005,0.121 -1.319,0.362 -0.31,0.242 -0.465,0.579 -0.465,1.012 0,0.365 0.168,0.67 0.506,0.916 0.337,0.242 0.893,0.479 1.668,0.711 0.774,0.228 1.396,0.49 1.866,0.786 0.469,0.292 0.813,0.629 1.032,1.012 0.219,0.378 0.328,0.822 0.328,1.333 0,0.829 -0.319,1.49 -0.957,1.982 -0.633,0.488 -1.495,0.732 -2.584,0.732 -0.72,0 -1.383,-0.132 -1.989,-0.397 -0.602,-0.269 -1.071,-0.638 -1.408,-1.107 -0.333,-0.47 -0.499,-1.016 -0.499,-1.641 h 1.729 c 0,0.565 0.187,1.003 0.561,1.313 0.373,0.31 0.909,0.465 1.606,0.465 0.602,0 1.053,-0.121 1.354,-0.363 0.305,-0.246 0.458,-0.569 0.458,-0.97 z m 11.938,-5.996 h -3.103 v 8.559 h -1.716 v -8.559 h -3.076 v -1.394 h 7.895 z m 10.565,3.842 c 0,0.975 -0.169,1.832 -0.506,2.571 -0.338,0.733 -0.821,1.298 -1.45,1.695 -0.624,0.392 -1.344,0.588 -2.16,0.588 -0.806,0 -1.526,-0.196 -2.16,-0.588 -0.629,-0.397 -1.116,-0.959 -1.463,-1.689 -0.341,-0.729 -0.515,-1.57 -0.519,-2.522 v -0.561 c 0,-0.97 0.171,-1.827 0.512,-2.57 0.347,-0.743 0.832,-1.31 1.457,-1.702 0.628,-0.397 1.348,-0.595 2.16,-0.595 0.811,0 1.529,0.196 2.153,0.588 0.629,0.387 1.114,0.948 1.456,1.682 0.342,0.729 0.515,1.579 0.52,2.549 z m -1.73,-0.519 c 0,-1.103 -0.21,-1.949 -0.629,-2.536 -0.415,-0.588 -1.005,-0.882 -1.77,-0.882 -0.748,0 -1.333,0.294 -1.757,0.882 -0.419,0.583 -0.634,1.41 -0.643,2.481 v 0.574 c 0,1.094 0.212,1.939 0.636,2.536 0.428,0.597 1.021,0.896 1.777,0.896 0.766,0 1.354,-0.292 1.764,-0.875 0.415,-0.583 0.622,-1.436 0.622,-2.557 z m 12.848,5.236 h -1.73 l -4.436,-7.062 v 7.062 h -1.73 v -9.953 h 1.73 l 4.45,7.089 v -7.089 h 1.716 z m 9.361,-4.443 h -4.088 v 3.062 h 4.779 v 1.381 h -6.508 v -9.953 h 6.46 v 1.394 h -4.731 v 2.748 h 4.088 z m 10.315,0.745 v 3.698 h -1.73 v -9.953 h 3.808 c 1.112,0 1.993,0.289 2.645,0.868 0.656,0.579 0.985,1.344 0.985,2.297 0,0.975 -0.322,1.734 -0.964,2.276 -0.638,0.543 -1.534,0.814 -2.687,0.814 z m 0,-1.388 h 2.078 c 0.615,0 1.084,-0.143 1.408,-0.431 0.323,-0.291 0.485,-0.711 0.485,-1.257 0,-0.538 -0.164,-0.967 -0.492,-1.286 -0.328,-0.323 -0.779,-0.489 -1.353,-0.499 h -2.126 z m 10.455,3.705 h 4.518 v 1.381 h -6.248 v -9.953 h 1.73 z m 13.052,-0.936 h -3.855 l -0.807,2.317 h -1.798 l 3.76,-9.953 h 1.552 l 3.767,9.953 h -1.805 z m -3.37,-1.395 h 2.885 l -1.442,-4.129 z m 16.519,3.712 h -1.73 l -4.436,-7.062 v 7.062 h -1.73 v -9.953 h 1.73 l 4.45,7.089 v -7.089 h 1.716 z"
          fill="black"></path>
        {/* Value Tracking Label */}
        <path
          d="m 143.802,186.8345 2.529,-7.786 h 1.9 l -3.582,9.953 h -1.674 l -3.569,-9.953 h 1.894 z m 12.109,-0.15 h -3.855 l -0.807,2.317 h -1.798 l 3.76,-9.953 h 1.552 l 3.766,9.953 h -1.804 z m -3.37,-1.395 h 2.885 l -1.443,-4.129 z m 10.353,2.331 h 4.518 v 1.381 h -6.248 v -9.953 h 1.73 z m 14.46,-8.572 v 6.651 c 0,1.058 -0.339,1.896 -1.018,2.516 -0.675,0.615 -1.575,0.923 -2.7,0.923 -1.14,0 -2.044,-0.303 -2.714,-0.909 -0.67,-0.611 -1.005,-1.457 -1.005,-2.537 v -6.644 h 1.723 v 6.658 c 0,0.665 0.168,1.174 0.506,1.524 0.337,0.351 0.833,0.527 1.49,0.527 1.33,0 1.996,-0.702 1.996,-2.106 v -6.603 z m 9.17,5.51 h -4.087 v 3.062 h 4.778 v 1.381 h -6.508 v -9.953 h 6.46 v 1.394 h -4.73 v 2.748 h 4.087 z m 15.367,-4.116 h -3.104 v 8.559 h -1.716 v -8.559 h -3.076 v -1.394 h 7.896 z m 6.469,4.724 h -1.927 v 3.835 h -1.73 v -9.953 h 3.5 c 1.149,0 2.035,0.257 2.659,0.772 0.625,0.515 0.937,1.26 0.937,2.236 0,0.665 -0.162,1.223 -0.486,1.674 -0.319,0.447 -0.765,0.791 -1.339,1.033 l 2.235,4.149 v 0.089 h -1.853 z m -1.927,-1.388 h 1.777 c 0.583,0 1.039,-0.145 1.367,-0.437 0.328,-0.296 0.492,-0.7 0.492,-1.21 0,-0.533 -0.152,-0.946 -0.458,-1.237 -0.3,-0.292 -0.752,-0.442 -1.353,-0.452 h -1.825 z m 14.078,2.906 h -3.856 l -0.806,2.317 h -1.798 l 3.76,-9.953 h 1.551 l 3.767,9.953 h -1.805 z m -3.37,-1.395 h 2.884 l -1.442,-4.129 z m 16.067,0.472 c -0.1,1.062 -0.492,1.891 -1.176,2.488 -0.683,0.592 -1.593,0.889 -2.727,0.889 -0.793,0 -1.493,-0.187 -2.099,-0.561 -0.602,-0.378 -1.066,-0.914 -1.394,-1.606 -0.329,-0.693 -0.5,-1.497 -0.513,-2.413 v -0.93 c 0,-0.939 0.166,-1.766 0.499,-2.481 0.333,-0.716 0.809,-1.267 1.429,-1.655 0.624,-0.387 1.344,-0.581 2.16,-0.581 1.098,0 1.982,0.299 2.652,0.896 0.67,0.597 1.06,1.44 1.169,2.529 h -1.723 c -0.082,-0.716 -0.291,-1.231 -0.629,-1.545 -0.332,-0.319 -0.822,-0.478 -1.469,-0.478 -0.752,0 -1.331,0.275 -1.737,0.827 -0.401,0.547 -0.606,1.351 -0.615,2.413 v 0.882 c 0,1.075 0.192,1.895 0.574,2.461 0.388,0.565 0.953,0.847 1.696,0.847 0.679,0 1.189,-0.152 1.531,-0.458 0.342,-0.305 0.558,-0.813 0.649,-1.524 z m 5.909,-1.06 -1.128,1.196 v 3.104 h -1.729 v -9.953 h 1.729 v 4.669 l 0.957,-1.183 2.912,-3.486 h 2.092 l -3.698,4.409 3.91,5.544 h -2.051 z m 9.3,4.3 h -1.723 v -9.953 h 1.723 z m 11.521,0 h -1.729 l -4.437,-7.062 v 7.062 h -1.729 v -9.953 h 1.729 l 4.451,7.089 v -7.089 h 1.715 z m 11.173,-1.292 c -0.36,0.469 -0.859,0.825 -1.497,1.066 -0.638,0.242 -1.362,0.363 -2.174,0.363 -0.834,0 -1.572,-0.189 -2.214,-0.568 -0.643,-0.378 -1.14,-0.918 -1.491,-1.62 -0.346,-0.706 -0.526,-1.529 -0.54,-2.468 v -0.779 c 0,-1.504 0.36,-2.677 1.08,-3.52 0.72,-0.848 1.725,-1.272 3.015,-1.272 1.107,0 1.987,0.274 2.639,0.82 0.651,0.547 1.043,1.336 1.175,2.366 h -1.695 c -0.191,-1.199 -0.886,-1.798 -2.085,-1.798 -0.775,0 -1.365,0.28 -1.77,0.841 -0.401,0.556 -0.609,1.374 -0.622,2.454 v 0.765 c 0,1.076 0.225,1.912 0.676,2.509 0.456,0.593 1.087,0.889 1.894,0.889 0.884,0 1.513,-0.201 1.887,-0.602 v -1.948 h -2.051 v -1.312 h 3.773 z"
          fill="black"></path>
        <path
          d="M493.406 62.7598C493.306 63.8216 492.914 64.651 492.23 65.248C491.547 65.8405 490.638 66.1367 489.503 66.1367C488.71 66.1367 488.01 65.9499 487.404 65.5762C486.803 65.1979 486.338 64.6624 486.01 63.9697C485.682 63.277 485.511 62.4727 485.497 61.5566V60.627C485.497 59.6882 485.663 58.861 485.996 58.1455C486.329 57.43 486.805 56.8786 487.425 56.4912C488.049 56.1038 488.769 55.9102 489.585 55.9102C490.683 55.9102 491.567 56.2087 492.237 56.8057C492.907 57.4027 493.297 58.2458 493.406 59.335H491.684C491.602 58.6195 491.392 58.1045 491.055 57.79C490.722 57.471 490.232 57.3115 489.585 57.3115C488.833 57.3115 488.254 57.5872 487.849 58.1387C487.448 58.6855 487.243 59.4899 487.233 60.5518V61.4336C487.233 62.5091 487.425 63.3294 487.808 63.8945C488.195 64.4596 488.76 64.7422 489.503 64.7422C490.182 64.7422 490.692 64.5895 491.034 64.2842C491.376 63.9788 491.592 63.4707 491.684 62.7598H493.406ZM504.401 61.2832C504.401 62.2585 504.233 63.1152 503.896 63.8535C503.558 64.5872 503.075 65.1523 502.446 65.5488C501.822 65.9408 501.102 66.1367 500.286 66.1367C499.479 66.1367 498.759 65.9408 498.126 65.5488C497.497 65.1523 497.009 64.5895 496.663 63.8604C496.321 63.1312 496.148 62.2904 496.144 61.3379V60.7773C496.144 59.8066 496.314 58.9499 496.656 58.207C497.003 57.4642 497.488 56.8968 498.112 56.5049C498.741 56.1084 499.461 55.9102 500.272 55.9102C501.084 55.9102 501.801 56.1061 502.426 56.498C503.055 56.8854 503.54 57.446 503.882 58.1797C504.224 58.9089 504.397 59.7588 504.401 60.7295V61.2832ZM502.672 60.7637C502.672 59.6608 502.462 58.8154 502.043 58.2275C501.628 57.6396 501.038 57.3457 500.272 57.3457C499.525 57.3457 498.939 57.6396 498.516 58.2275C498.096 58.8109 497.882 59.638 497.873 60.709V61.2832C497.873 62.377 498.085 63.2223 498.509 63.8193C498.937 64.4163 499.53 64.7148 500.286 64.7148C501.052 64.7148 501.64 64.4232 502.05 63.8398C502.465 63.2565 502.672 62.4043 502.672 61.2832V60.7637ZM515.52 66H513.79L509.354 58.9385V66H507.624V56.0469H509.354L513.804 63.1357V56.0469H515.52V66ZM524.799 61.7822H520.793V66H519.063V56.0469H525.387V57.4414H520.793V60.4014H524.799V61.7822ZM530.086 66H528.363V56.0469H530.086V66ZM537.369 62.165H535.441V66H533.712V56.0469H537.212C538.36 56.0469 539.247 56.3044 539.871 56.8193C540.495 57.3343 540.808 58.0794 540.808 59.0547C540.808 59.7201 540.646 60.2783 540.322 60.7295C540.003 61.1761 539.557 61.5202 538.982 61.7617L541.218 65.9111V66H539.365L537.369 62.165ZM535.441 60.7773H537.219C537.802 60.7773 538.258 60.6315 538.586 60.3398C538.914 60.0436 539.078 59.6403 539.078 59.1299C539.078 58.5967 538.925 58.1842 538.62 57.8926C538.319 57.6009 537.868 57.4505 537.267 57.4414H535.441V60.7773ZM546.19 56.0469L549.062 63.6758L551.926 56.0469H554.161V66H552.438V62.7188L552.609 58.3301L549.67 66H548.433L545.5 58.3369L545.671 62.7188V66H543.948V56.0469H546.19Z"
          fill="black"></path>
        <path
          d="M665.359 66V56.0469H668.299C669.178 56.0469 669.958 56.2428 670.637 56.6348C671.32 57.0267 671.849 57.5827 672.223 58.3027C672.596 59.0228 672.783 59.8477 672.783 60.7773V61.2764C672.783 62.2197 672.594 63.0492 672.216 63.7646C671.842 64.4801 671.307 65.0316 670.609 65.4189C669.917 65.8063 669.121 66 668.224 66H665.359ZM667.089 57.4414V64.6191H668.217C669.124 64.6191 669.819 64.3366 670.302 63.7715C670.789 63.2018 671.038 62.3861 671.047 61.3242V60.7705C671.047 59.6904 670.812 58.8656 670.343 58.2959C669.873 57.7262 669.192 57.4414 668.299 57.4414H667.089ZM681.823 61.5566H677.735V64.6191H682.514V66H676.006V56.0469H682.466V57.4414H677.735V60.1895H681.823V61.5566ZM687.151 64.6191H691.67V66H685.422V56.0469H687.151V64.6191ZM696.321 66H694.599V56.0469H696.321V66ZM703.454 63.833L705.983 56.0469H707.884L704.302 66H702.627L699.059 56.0469H700.952L703.454 63.833ZM716.329 61.5566H712.241V64.6191H717.02V66H710.512V56.0469H716.972V57.4414H712.241V60.1895H716.329V61.5566ZM723.585 62.165H721.657V66H719.928V56.0469H723.428C724.576 56.0469 725.463 56.3044 726.087 56.8193C726.711 57.3343 727.023 58.0794 727.023 59.0547C727.023 59.7201 726.862 60.2783 726.538 60.7295C726.219 61.1761 725.772 61.5202 725.198 61.7617L727.434 65.9111V66H725.581L723.585 62.165ZM721.657 60.7773H723.435C724.018 60.7773 724.474 60.6315 724.802 60.3398C725.13 60.0436 725.294 59.6403 725.294 59.1299C725.294 58.5967 725.141 58.1842 724.836 57.8926C724.535 57.6009 724.084 57.4505 723.482 57.4414H721.657V60.7773Z"
          fill="black"></path>
        <path
          d="M852.792 61.5566H848.704V64.6191H853.482V66H846.975V56.0469H853.435V57.4414H848.704V60.1895H852.792V61.5566ZM858.633 56.0469L861.504 63.6758L864.368 56.0469H866.604V66H864.881V62.7188L865.052 58.3301L862.112 66H860.875L857.942 58.3369L858.113 62.7188V66H856.391V56.0469H858.633ZM870.154 66V56.0469H873.565C874.691 56.0469 875.548 56.2725 876.136 56.7236C876.724 57.1748 877.018 57.847 877.018 58.7402C877.018 59.196 876.895 59.6061 876.648 59.9707C876.402 60.3353 876.042 60.6178 875.568 60.8184C876.106 60.9642 876.521 61.2399 876.812 61.6455C877.109 62.0465 877.257 62.5296 877.257 63.0947C877.257 64.029 876.956 64.7467 876.354 65.248C875.757 65.7493 874.901 66 873.784 66H870.154ZM871.884 61.5088V64.6191H873.805C874.347 64.6191 874.771 64.4847 875.076 64.2158C875.382 63.9469 875.534 63.5732 875.534 63.0947C875.534 62.0602 875.006 61.5316 873.948 61.5088H871.884ZM871.884 60.2373H873.579C874.117 60.2373 874.536 60.1165 874.837 59.875C875.142 59.6289 875.295 59.2826 875.295 58.8359C875.295 58.3438 875.154 57.9883 874.871 57.7695C874.593 57.5508 874.158 57.4414 873.565 57.4414H871.884V60.2373ZM886.304 61.5566H882.216V64.6191H886.994V66H880.486V56.0469H886.946V57.4414H882.216V60.1895H886.304V61.5566ZM889.902 66V56.0469H892.842C893.721 56.0469 894.501 56.2428 895.18 56.6348C895.863 57.0267 896.392 57.5827 896.766 58.3027C897.139 59.0228 897.326 59.8477 897.326 60.7773V61.2764C897.326 62.2197 897.137 63.0492 896.759 63.7646C896.385 64.4801 895.85 65.0316 895.152 65.4189C894.46 65.8063 893.664 66 892.767 66H889.902ZM891.632 57.4414V64.6191H892.76C893.667 64.6191 894.362 64.3366 894.845 63.7715C895.332 63.2018 895.581 62.3861 895.59 61.3242V60.7705C895.59 59.6904 895.355 58.8656 894.886 58.2959C894.416 57.7262 893.735 57.4414 892.842 57.4414H891.632Z"
          fill="black"></path>
        <circle cx="130" cy="88" r="14" fill="white" stroke="#6CB33F" strokeWidth="4"
          onMouseOver={mouseOverEvent('Aim')} onMouseOut={mouseLeaveEvent} onClick={handleClick('Aim')}
        ></circle>
        <path
          d="M 126.252 91.893 L 122.082 87.723 L 120.662 89.133 L 126.252 94.723 L 138.252 82.723 L 136.842 81.313 L 126.252 91.893 Z"
          fill="#6CB33F">
        </path>

        <rect width="32" height="32" transform="translate(280 72)" fill="white"></rect>
        <circle cx="296" cy="88" r="14" stroke={isStateApproved('Build') ? "#6CB33F" : "#000f56"}
          fill={isStateRequested('Build') ? "#BDE1A4" : "white"} strokeWidth="4"
          onMouseOver={mouseOverEvent('Build')} onMouseOut={mouseLeaveEvent} onClick={handleClick('Build')}
        ></circle>
        {isRevertAllowed('Build') ?
          <path
            d="M 292.721 84.97 L 294.141 86.39 L 290.551 89.97 L 299.721 89.97 L 299.721 79.97 L 301.721 79.97 L 301.721 91.97 L 290.551 91.97 L 294.141 95.55 L 292.721 96.97 L 286.721 90.97 L 292.721 84.97 Z"
            fill="#000f56"></path>
          :
          isStateApproved('Build') ?
            <path
              d="M 291.481 91.431 L 287.311 87.261 L 285.891 88.671 L 291.481 94.261 L 303.481 82.261 L 302.071 80.851 L 291.481 91.431 Z"
              fill="#6CB33F"></path> :
            null
        }
        {showForwardHover('Build') ?
          <path
            d="M 303.5 90.421 L 297.5 96.421 L 296.08 95.001 L 299.67 91.421 L 288.5 91.421 L 288.5 79.421 L 290.5 79.421 L 290.5 89.421 L 299.67 89.421 L 296.08 85.841 L 297.5 84.421 L 303.5 90.421 Z"
            fill="#000f56"></path> : null
        }
        <rect width="32" height="32" transform="translate(280 120)" fill="white"></rect>
        <circle cx="296" cy="136" r="14" stroke={isStateApproved('Milestone') ? "#6CB33F" : "#000f56"}
          fill={isStateRequested('Milestone') ? "#BDE1A4" : "white"} strokeWidth="4"
          onMouseOver={mouseOverEvent('Milestone')} onMouseOut={mouseLeaveEvent} onClick={handleClick('Milestone')}
        ></circle>
        {isStateApproved('Milestone') ?
          <path
            d="M 292.159 139.874 L 287.989 135.704 L 286.569 137.114 L 292.159 142.704 L 304.159 130.704 L 302.749 129.294 L 292.159 139.874 Z"
            fill="#6CB33F"></path> :
          null
        }
        {showForwardHover('Milestone') ?
          <path
            d="M 303.303 139.161 L 297.303 145.161 L 295.883 143.741 L 299.473 140.161 L 288.303 140.161 L 288.303 128.161 L 290.303 128.161 L 290.303 138.161 L 299.473 138.161 L 295.883 134.581 L 297.303 133.161 L 303.303 139.161 Z"
            fill="#000f56"></path> : null
        }
        <rect width="32" height="32" transform="translate(280 168)" fill="white"></rect>
        <circle cx="296" cy="184" r="14" stroke={isStateApproved('Value') ? "#6CB33F" : "#000f56"}
          fill={isStateRequested('Value') ? "#BDE1A4" : "white"} strokeWidth="4"
          onMouseOver={mouseOverEvent('Value')} onMouseOut={mouseLeaveEvent} onClick={handleClick('Value')}></circle>
        {isStateApproved('Value') ?
          <path
            d="M 292.124 187.243 L 287.954 183.073 L 286.534 184.483 L 292.124 190.073 L 304.124 178.073 L 302.714 176.663 L 292.124 187.243 Z"
            fill="#6CB33F"></path> :
          null
        }
        {showForwardHover('Value') ?
          <path
            d="M 303.534 186.938 L 297.534 192.938 L 296.114 191.518 L 299.704 187.938 L 288.534 187.938 L 288.534 175.938 L 290.534 175.938 L 290.534 185.938 L 299.704 185.938 L 296.114 182.358 L 297.534 180.938 L 303.534 186.938 Z"
            fill="#000f56"></path> : null
        }
        <rect width="32" height="32" transform="translate(280 216)" fill="white"></rect>
        <circle cx="520" cy="88" r="14" stroke={isStateApproved('Confirm') ? "#6CB33F" : "#000f56"}
          fill={isStateRequested('Confirm') ? "#BDE1A4" : "white"} strokeWidth="4"
          onMouseOver={mouseOverEvent('Confirm')} onMouseOut={mouseLeaveEvent} onClick={handleClick('Confirm')}

        ></circle>
        {isRevertAllowed('Confirm') ?
          <path
            d="M 516.185 85.837 L 517.605 87.257 L 514.015 90.837 L 523.185 90.837 L 523.185 80.837 L 525.185 80.837 L 525.185 92.837 L 514.015 92.837 L 517.605 96.417 L 516.185 97.837 L 510.185 91.837 L 516.185 85.837 Z"
            fill="#000f56"></path>
          :
          isStateApproved('Confirm') ?
            <path
              d="M 516.408 91.485 L 512.238 87.315 L 510.818 88.725 L 516.408 94.315 L 528.408 82.315 L 526.998 80.905 L 516.408 91.485 Z"
              fill="#6CB33F"></path> : null
        }
        {showForwardHover('Confirm') ?
          <path
            d="M 527.789 91.635 L 521.789 97.635 L 520.369 96.215 L 523.959 92.635 L 512.789 92.635 L 512.789 80.635 L 514.789 80.635 L 514.789 90.635 L 523.959 90.635 L 520.369 87.055 L 521.789 85.635 L 527.789 91.635 Z"
            fill="#000f56"></path> : null
        }
        {isRevertAllowed('Confirm') ?
          <path
            d="M 516.185 85.837 L 517.605 87.257 L 514.015 90.837 L 523.185 90.837 L 523.185 80.837 L 525.185 80.837 L 525.185 92.837 L 514.015 92.837 L 517.605 96.417 L 516.185 97.837 L 510.185 91.837 L 516.185 85.837 Z"
            fill="#000f56"></path>
          : null
        }
        <rect width="32" height="32" transform="translate(680 72)" fill="white"></rect>
        <circle cx="696" cy="88" r="14" stroke={isStateApproved('Deliver') ? "#6CB33F" : "#000f56"}
          fill={isStateRequested('Deliver') ? "#BDE1A4" : "white"} strokeWidth="4"
          onMouseOver={mouseOverEvent('Deliver')} onMouseOut={mouseLeaveEvent} onClick={handleClick('Deliver')}
        ></circle>
        {isRevertAllowed('Deliver') ?
          <path
            d="M 692.971 84.483 L 694.391 85.903 L 690.801 89.483 L 699.971 89.483 L 699.971 79.483 L 701.971 79.483 L 701.971 91.483 L 690.801 91.483 L 694.391 95.063 L 692.971 96.483 L 686.971 90.483 L 692.971 84.483 Z"
            fill="#000f56"></path>
          :
          isStateApproved('Deliver') ?
            <path
              d="M 692.507 91.461 L 688.337 87.291 L 686.917 88.701 L 692.507 94.291 L 704.507 82.291 L 703.097 80.881 L 692.507 91.461 Z"
              fill="#6CB33F"></path> : null
        }
        {showForwardHover('Deliver') ?
          <path
            d="M 527.789 91.635 L 521.789 97.635 L 520.369 96.215 L 523.959 92.635 L 512.789 92.635 L 512.789 80.635 L 514.789 80.635 L 514.789 90.635 L 523.959 90.635 L 520.369 87.055 L 521.789 85.635 L 527.789 91.635 Z"
            fill="#000f56" transform="matrix(1, 0, 0, 1, 176.413147, 0.371698)"></path> : null
        }

        <rect width="32" height="32" transform="translate(856 72)" fill="white"></rect>
        <circle cx="872" cy="88" r="14" stroke={isStateApproved('Embed') ? "#6CB33F" : "#000f56"}
          fill={isStateRequested('Embed') ? "#BDE1A4" : "white"} strokeWidth="4"
          onMouseOver={mouseOverEvent('Embed')} onMouseOut={mouseLeaveEvent} onClick={handleClick('Embed')}
        ></circle>
        {isRevertAllowed('Embed') ?
          <path d="M 868.422 85.496 L 869.842 86.916 L 866.252 90.496 L 875.422 90.496 L 875.422 80.496 L 877.422 80.496 L 877.422 92.496 L 866.252 92.496 L 869.842 96.076 L 868.422 97.496 L 862.422 91.496 L 868.422 85.496 Z" fill="#000f56"></path>
          : isStateApproved('Embed') ?
            <path
              d="M 868.168 92.341 L 863.998 88.171 L 862.578 89.581 L 868.168 95.171 L 880.168 83.171 L 878.758 81.761 L 868.168 92.341 Z"
              fill="#6CB33F"></path> : null}
        {showForwardHover('Embed') ?
          <path
            d="M 880.087 92.082 L 874.087 98.082 L 872.667 96.662 L 876.257 93.082 L 865.087 93.082 L 865.087 81.082 L 867.087 81.082 L 867.087 91.082 L 876.257 91.082 L 872.667 87.502 L 874.087 86.082 L 880.087 92.082 Z"
            fill="#000f56"></path> : null
        }
      </svg>
    </div>
    {getApprovalButtons()}
  </div>);
}
